/**
 * Le son se débloque au geste, et se redébloque autant de fois qu'il le faut.
 *
 * L'`AudioContext` est construit au montage du fournisseur du studio, donc
 * avant tout geste : le navigateur refuse ce `resume()`-là et laisse le
 * contexte `suspended`, avec `currentTime` **gelé**. Tout ce qui lit l'horloge
 * audio s'arrête alors sans rien dire — le transport n'avance pas, et le
 * décompte, qui pré-calcule ses temps de battue dessus, allume sa première
 * pastille et attend un instant qui n'arrivera jamais.
 *
 * Ça se réparait d'abord porte par porte : le carton d'accueil du studio
 * réveillait le contexte dans son `onClick`, l'extrait d'une fiche dans le
 * sien. Le jour où le carton a cessé de s'afficher pour qui arrive avec un
 * morceau déjà chargé, le parcours des notes s'est retrouvé sans réveil —
 * bandeau, pastilles, et rien. Une porte de plus, un oubli de plus : ce n'est
 * pas une propriété d'un bouton, c'est une propriété de la session.
 *
 * Les écoutes sont posées en capture, donc avant les gestionnaires React : le
 * geste qui ouvre un morceau débloque le son *puis* charge la pièce, dans cet
 * ordre. Les événements couvrent la souris, le tactile et le clavier seul.
 *
 * Le doigt, deux fois : au contact et au lever. WebKit accepte de réveiller le
 * son dès le contact ; Chrome, non — un doigt n'y autorise le son qu'au lever
 * (`pointerup`, `touchend`), le contact ne compte que pour la souris. Sans le
 * lever, le premier tap ne réveillait rien sous Android : le clic sur
 * « Regarder » ouvrait un replay muet, sans décompte ni lecture (2026-09-29).
 *
 * On ne se retire pas au premier geste mais au premier `running` : un
 * `resume()` peut être refusé, et abandonner après un refus rendrait le
 * silence définitif.
 *
 * Et le réveil ne vaut pas une fois pour toutes. Sur iPad, le contexte quitte
 * `running` bien après le premier geste : l'app passe en arrière-plan, l'écran
 * se verrouille, une autre application prend la sortie audio, et Safari pose
 * l'état `interrupted` — qui ne figure pas dans le `AudioContextState` du DOM
 * standard, d'où le `state !== "running"` plutôt qu'une liste d'états.
 * `currentTime` regèle, et le décompte retombe sur exactement le même symptôme
 * qu'au premier jour : une pastille allumée devant un temps qui n'arrive pas,
 * le passage en rythme qui ne part pas, et pour seul remède un rechargement de
 * l'application. On suit donc `statechange` pour la vie du fournisseur et on
 * réarme les écoutes chaque fois que le contexte sort de `running` — le geste
 * suivant, quel qu'il soit, le ramène.
 */

/**
 * Ce que cette fonction demande d'un `AudioContext`, et rien de plus.
 *
 * Les trois dernières sont facultatives : elles ne servent qu'au coup de
 * démarreur de `kick`, et un double de test n'a pas à les porter.
 */
export type UnlockableAudioContext = Pick<
  AudioContext,
  "state" | "resume" | "addEventListener" | "removeEventListener"
> &
  Partial<Pick<AudioContext, "createBuffer" | "createBufferSource" | "destination" | "sampleRate">>;

/** Ce qu'elle demande de `window` : de quoi écouter un geste en capture. */
export type GestureTarget = Pick<Window, "addEventListener" | "removeEventListener">;

const GESTURES = ["pointerdown", "touchstart", "pointerup", "touchend", "keydown"] as const;

/**
 * Arme le déblocage du son sur `target` pour la vie de `context`.
 *
 * Renvoie de quoi tout retirer.
 */
export function unlockAudioOnGesture(
  context: UnlockableAudioContext,
  target: GestureTarget,
): () => void {
  let armed = false;

  function arm(): void {
    if (armed || context.state === "closed") {
      return;
    }

    armed = true;
    for (const type of GESTURES) {
      target.addEventListener(type, unlock, true);
    }
  }

  function disarm(): void {
    if (!armed) {
      return;
    }

    armed = false;
    for (const type of GESTURES) {
      target.removeEventListener(type, unlock, true);
    }
  }

  /**
   * Le coup de démarreur, dans le geste.
   *
   * `resume()` seul suffit à un contexte que la politique d'autoplay retient.
   * Il ne suffit pas toujours à un contexte que WebKit a **interrompu** : il
   * repasse à `running`, son horloge repart, et l'unité audio sous lui reste
   * éteinte — rien ne sort, sans que rien ne le dise. Démarrer une source
   * d'une seule trame depuis le geste la rallume ; c'est le geste de
   * déverrouillage que toutes les bibliothèques audio du web font sur iOS, et
   * il ne coûte qu'un nœud jeté aussitôt.
   *
   * Silencieux sur un contexte qui n'a pas ces méthodes — un double de test —
   * et sur un contexte fermé entre-temps.
   */
  function kick(): void {
    try {
      const source = context.createBufferSource?.();

      if (source === undefined || context.destination === undefined) {
        return;
      }

      source.buffer = context.createBuffer?.(1, 1, context.sampleRate ?? 44100) ?? null;
      source.connect(context.destination);
      source.start(0);
    } catch {
      // Rien à réparer : c'est un bonus, et `resume()` reste le geste principal.
    }
  }

  function unlock(): void {
    if (context.state === "closed") {
      disarm();

      return;
    }

    kick();

    void context.resume().then(
      () => {
        if (context.state === "running") {
          disarm();
        }
      },
      () => {
        // Refusé : on garde l'abonnement, le geste suivant réessaiera.
      },
    );
  }

  function onStateChange(): void {
    if (context.state === "running" || context.state === "closed") {
      disarm();

      return;
    }

    arm();
  }

  context.addEventListener("statechange", onStateChange);
  onStateChange();

  return () => {
    context.removeEventListener("statechange", onStateChange);
    disarm();
  };
}
